import HttpClient from '../http/HttpClient';
import { LEAD_SERVICE_HOST, LEAD_API_PREFIX } from '../constants/RestConstants';
import { PaginationConst } from '../constants/AppConstant';
import type {
  CapitalContribution,
  CapitalManagementSummary,
  CapitalPartner,
  CapitalsDashboard,
  PlanCapitalAllocation,
  StockPlanResult,
} from '../../models/StockPickTypes';

type ApiEnvelope<T> = { data: T; message?: string; statusCode: number };

export interface CapitalPartnerListResponse {
  result: CapitalPartner[];
  totalCount: number;
  message?: string;
  code: number;
}

const BASE = `${LEAD_API_PREFIX}/capitals`;

export async function getCapitalSummary(): Promise<CapitalManagementSummary> {
  const response = await HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/capital-summary`,
  });
  return (response.data as ApiEnvelope<CapitalManagementSummary>).data;
}

/**
 * Dedicated dashboard read API for the Capital Partners tile on the admin lending
 * dashboard and the on-demand "fetch partner capital" button on the personal
 * net worth tracker.
 */
export async function getCapitalsDashboard(): Promise<CapitalsDashboard> {
  const response = await HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/dashboard`,
  });
  return (response.data as ApiEnvelope<CapitalsDashboard>).data;
}

export async function getCapitalPartnersPage(
  page: number = 0,
  size: number = PaginationConst.PAGE_SIZE
): Promise<CapitalPartnerListResponse> {
  const response = await HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/partners`,
    params: { page, size, sort: 'name,asc' },
  });
  return response.data as CapitalPartnerListResponse;
}

export async function getCapitalPartnersBrief(): Promise<CapitalPartner[]> {
  const response = await HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/partners/brief`,
  });
  return (response.data as ApiEnvelope<CapitalPartner[]>).data;
}

export async function getCapitalPartner(id: string): Promise<CapitalPartner> {
  const response = await HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/partners/${encodeURIComponent(id)}`,
  });
  return (response.data as ApiEnvelope<CapitalPartner>).data;
}

export async function createCapitalPartner(body: CapitalPartner): Promise<CapitalPartner> {
  const response = await HttpClient.POST({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/partners`,
    data: body,
  });
  return (response.data as ApiEnvelope<CapitalPartner>).data;
}

export async function updateCapitalPartner(id: string, body: CapitalPartner): Promise<CapitalPartner> {
  const response = await HttpClient.PUT({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/partners/${encodeURIComponent(id)}`,
    data: { ...body, id },
  });
  return (response.data as ApiEnvelope<CapitalPartner>).data;
}

export async function deleteCapitalPartner(id: string): Promise<void> {
  await HttpClient.DELETE({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/partners/${encodeURIComponent(id)}`,
  });
}

export async function addCapitalContribution(partnerId: string, body: CapitalContribution): Promise<CapitalContribution> {
  const response = await HttpClient.POST({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/partners/${encodeURIComponent(partnerId)}/contributions`,
    data: body,
  });
  return (response.data as ApiEnvelope<CapitalContribution>).data;
}

export async function deleteCapitalContribution(partnerId: string, contributionId: string): Promise<void> {
  await HttpClient.DELETE({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/partners/${encodeURIComponent(partnerId)}/contributions/${encodeURIComponent(contributionId)}`,
  });
}

export async function getPlanAllocations(planId: string): Promise<PlanCapitalAllocation[]> {
  const response = await HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/stock-plans/${encodeURIComponent(planId)}/allocations`,
  });
  return (response.data as ApiEnvelope<PlanCapitalAllocation[]>).data;
}

export async function savePlanAllocations(planId: string, allocations: PlanCapitalAllocation[]): Promise<PlanCapitalAllocation[]> {
  const response = await HttpClient.PUT({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/stock-plans/${encodeURIComponent(planId)}/allocations`,
    data: allocations,
  });
  return (response.data as ApiEnvelope<PlanCapitalAllocation[]>).data;
}

export interface GeneratePartnerPortalPasswordResult {
  partnerId: string;
  loginPhone: string;
  plainPassword: string;
  message: string;
}

export async function generatePartnerPortalPassword(
  partnerId: string
): Promise<GeneratePartnerPortalPasswordResult> {
  const response = await HttpClient.POST({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/partners/${encodeURIComponent(partnerId)}/portal/generate-password`,
    data: {},
  });
  return (response.data as ApiEnvelope<GeneratePartnerPortalPasswordResult>).data;
}

export async function getPlanResult(planId: string): Promise<StockPlanResult | null> {
  const response = await HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/stock-plans/${encodeURIComponent(planId)}/result`,
  });
  return (response.data as ApiEnvelope<StockPlanResult | null>).data;
}

export async function savePlanResult(planId: string, body: StockPlanResult): Promise<StockPlanResult> {
  const response = await HttpClient.PUT({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/stock-plans/${encodeURIComponent(planId)}/result`,
    data: body,
  });
  return (response.data as ApiEnvelope<StockPlanResult>).data;
}

export async function savePlanProfitOverride(
  planId: string,
  profitOverride: number | null
): Promise<StockPlanResult> {
  const response = await HttpClient.PUT({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/stock-plans/${encodeURIComponent(planId)}/result/profit-override`,
    data: { profitOverride },
  });
  return (response.data as ApiEnvelope<StockPlanResult>).data;
}
